import { TFunction } from 'i18next';
import {
  intlAssetValueFormatting,
  getPrecisionFloatValue,
  intlFiatValueFormatting,
  getRoundedPrecision,
} from '@bts-web/utils-formatting';
import { AssetType, getTradeTranslations, isBpfsAsset } from '.';
import { NonNullableTransactionData } from './reusableTypes';
import { NEXT_ROUTES } from '@bts-web/utils-next-infrastructure';
import { TransactionStatus } from '@bts-web/data-layer/server';
import { ReactNode } from 'react';
import { PendingContent } from '../FinishTrade/FinishTradeController/subcomponents/PendingContent';
import { SuccessBuyContent } from '../FinishTrade/FinishTradeController/subcomponents/SuccessBuyContent';
import { SuccessSellContent } from '../FinishTrade/FinishTradeController/subcomponents/SuccessSellContent';
import { FailContent } from '../FinishTrade/FinishTradeController/subcomponents/FailContent';
import { getAppConfig } from '@bts-web/core-app-config';

export type TradeMessage = {
  title: string;
  subtitle?: string;
  statusTitleIcon?: string;
  poweredByText?: string;
  statusContent?: ReactNode;
  primaryButton: {
    text: string;
    url: string;
  };
  secondaryButton?: {
    text: string;
    url: string;
  };
};

// TODO remove exclusion after FE implementation
// CREDITED is a fiat-transfer status, not surfaced on the buy/sell trade screens.
export type ActiveTransactionStatus = Exclude<
  TransactionStatus,
  | 'CREATED'
  | 'EXPIRED'
  | 'REFUNDED'
  | 'NOT_CONFIRMED'
  | 'CANCELED'
  | 'REJECTED'
  | 'CREDITED'
>;

export function getTradeMessages({
  translationUtil,
  locale,
  hasTaxOfflineWarning,
  transactionData,
}: {
  translationUtil: TFunction;
  locale: string;
  hasTaxOfflineWarning: boolean;
  transactionData: NonNullableTransactionData;
}): Record<NonNullable<ActiveTransactionStatus>, TradeMessage> {
  const { asset } = transactionData;

  const isBuy = transactionData?.__typename === 'BuyTransaction';

  const isSell = transactionData?.__typename === 'SellTransaction';

  const isBpf = isBpfsAsset(
    (transactionData.sourceAsset
      ? transactionData.sourceAsset.__typename
      : asset!.__typename) as AssetType,
  );

  const { currency } = getAppConfig();

  const translations = getTradeTranslations(translationUtil);

  const assetAmount = getPrecisionFloatValue(transactionData.assetAmount);

  const formattedAssetAmount = intlAssetValueFormatting(assetAmount, {
    locale,
    fractionDigits: transactionData.assetAmount?.precision,
  });

  const formatedAmount = intlAssetValueFormatting(
    getPrecisionFloatValue(transactionData.assetAmount),
    {
      locale: locale,
      fractionDigits: transactionData?.assetAmount?.precision,
    },
  );

  const formatedSourceAmount =
    transactionData.sourceAmount &&
    intlAssetValueFormatting(
      getPrecisionFloatValue(transactionData.sourceAmount),
      {
        locale: locale,
        fractionDigits: transactionData?.assetAmount?.precision,
      },
    );

  const fiatAmount = intlFiatValueFormatting(
    getRoundedPrecision(transactionData.fiatAmount),
    { locale, currency },
  );

  const totalAmount = intlFiatValueFormatting(
    getRoundedPrecision(transactionData.totalAmount),
    { locale, currency },
  );

  const taxAmount = intlFiatValueFormatting(
    getRoundedPrecision(transactionData.taxAmount),
    { locale, currency },
  );

  const taxWithheldMessage =
    Number(transactionData?.taxAmount?.value) > 0
      ? translationUtil('amount_tax_withheld', { taxamount: `≈ ${taxAmount}` })
      : undefined;

  const poweredByText = isBpf
    ? translations.poweredByBpfs
    : translations.poweredByTenant;

  if (isSell) {
    const subtitle = translationUtil('success_sale_subtitle', {
      amount: formatedAmount,
      assetName: transactionData?.asset?.name,
      fiatAmount: totalAmount,
      interpolation: {
        escapeValue: false,
      },
    });

    return {
      FINISHED: {
        title: translations.successSaleTitle,
        statusContent: (
          <SuccessSellContent
            warningTaxNotFinalMessage={
              translations.sellScreenTaxNotFinalWarning
            }
            hasTaxOfflineWarning={hasTaxOfflineWarning}
            subtitleMessage={subtitle}
            taxWithheldMessage={taxWithheldMessage}
          />
        ),
        poweredByText,
        primaryButton: {
          text: translations.okGoToPortfolio,
          url: NEXT_ROUTES.PORTFOLIO,
        },
        secondaryButton: transactionData.id
          ? {
              text: translations.viewDetails,
              url: `${NEXT_ROUTES.TRANSACTIONS_DETAILS.replace('[id]', transactionData.id)}?transactionType=SELL`,
            }
          : undefined,
      },
      FAILED: {
        title: translations.transactionFailTitle,
        poweredByText,
        statusContent: (
          <FailContent subtitleMessage={translations.failCardText} />
        ),
        primaryButton: {
          text: translations.backToPortfolioButton,
          url: NEXT_ROUTES.PORTFOLIO,
        },
      },
      IN_PROGRESS: {
        title: translations.pendingSellPageTitle,
        statusContent: (
          <PendingContent
            subtitleMessage={translationUtil('pending_sell_subtitle', {
              assetamount: `${assetAmount} ${transactionData.asset?.symbol}`,
              fiatamount: totalAmount,
            })}
          />
        ),
        poweredByText,
        primaryButton: {
          text: translations.goToPortfolio,
          url: NEXT_ROUTES.PORTFOLIO,
        },
      },
    };
  }

  if (isBuy) {
    const subtitle = translationUtil('success_buy_subtitle', {
      amount: formatedAmount,
      assetName: transactionData?.asset?.name,
      fiatAmount: fiatAmount,
      interpolation: {
        escapeValue: false,
      },
    });

    return {
      FINISHED: {
        title: translations.successBuyTitle,
        statusContent: (
          <SuccessBuyContent
            subtitleMessage={subtitle}
            nowAvailableMessage={translations.successScreenNowAvailable}
          />
        ),
        poweredByText,
        primaryButton: {
          text: translations.okGoToPortfolio,
          url: NEXT_ROUTES.PORTFOLIO,
        },
        secondaryButton: transactionData.id
          ? {
              text: translations.viewDetails,
              url: `${NEXT_ROUTES.TRANSACTIONS_DETAILS.replace('[id]', transactionData.id)}?transactionType=BUY`,
            }
          : undefined,
      },
      FAILED: {
        title: translations.transactionFailTitle,
        poweredByText,
        statusContent: (
          <FailContent subtitleMessage={translations.failCardText} />
        ),
        primaryButton: {
          text: translations.backToPortfolioButton,
          url: NEXT_ROUTES.PORTFOLIO,
        },
      },
      IN_PROGRESS: {
        title: translations.pendingBuyPageTitle,
        statusContent: (
          <PendingContent
            subtitleMessage={translationUtil('pending_buy_subtitle', {
              assetamount: `${assetAmount} ${transactionData.asset?.symbol}`,
              fiatamount: fiatAmount,
            })}
          />
        ),
        poweredByText,
        primaryButton: {
          text: translations.goToPortfolio,
          url: NEXT_ROUTES.PORTFOLIO,
        },
      },
    };
  }

  const subtitle = translationUtil('swap_success_subtitle', {
    sourceAssetAmount: `${formatedSourceAmount} ${transactionData.sourceAsset?.symbol}`,
    targetAssetAmount: `${formatedAmount} ${transactionData.asset?.symbol}`,
    interpolation: {
      escapeValue: false,
    },
  });

  return {
    FINISHED: {
      title: translations.swapSuccessTitle,
      statusContent: (
        <SuccessSellContent
          warningTaxNotFinalMessage={translations.sellScreenTaxNotFinalWarning}
          hasTaxOfflineWarning={hasTaxOfflineWarning}
          subtitleMessage={subtitle}
          taxWithheldMessage={taxWithheldMessage}
        />
      ),
      poweredByText,
      primaryButton: {
        text: translations.viewTransaction,
        url: NEXT_ROUTES.PORTFOLIO,
      },
    },
    FAILED: {
      title: translations.swapFailedTitle,
      poweredByText,
      statusContent: (
        <FailContent subtitleMessage={translations.failCardText} />
      ),
      primaryButton: {
        text: translations.backToPortfolioButton,
        url: NEXT_ROUTES.PORTFOLIO,
      },
    },
    IN_PROGRESS: {
      title: translationUtil('swap_submitted_title', {
        targetAssetAmount: formattedAssetAmount,
      }),
      statusContent: (
        <PendingContent
          subtitleMessage={translationUtil('swap_submitted_subtitle')}
        />
      ),
      poweredByText,
      primaryButton: {
        text: translations.goToPortfolio,
        url: NEXT_ROUTES.PORTFOLIO,
      },
    },
  };
}
